<script setup lang="ts">
import { NButton, NEmpty, NSpin } from 'naive-ui'
import type { SidebarSearchResult } from '../composables/useSidebarSearch'

// FTS5 returns snippets bracketed by these custom markers (set in
// `Wiki::Search.snippet_marker`). The renderer below replaces them
// with `<mark>` tags AFTER HTML-escaping the snippet body, so the
// markup we inject is bounded to those two tags.
const MARK_OPEN = '«hl»'
const MARK_CLOSE = '«/hl»'

// Search-mode results panel for WikiSidebar. Owns the searching /
// error / empty / results template branches that appeared inline in
// WikiSidebar.vue before this branch — pulled out so the parent stays
// under the 300-line limit and the FTS-results UI gets its own
// testable boundary. The composable refs flow in as props so the
// parent's search input and the results stay synced via the shared
// `useSidebarSearch` instance up there.
defineProps<{
  results: readonly SidebarSearchResult[]
  searching: boolean
  error: string | null
  query: string
  // The active category-filter label, when the parent scopes search to a
  // category. Null/absent = wiki-wide search. Drives the
  // zero-result copy + the "Search everywhere" escape.
  activeFilterLabel?: string | null
}>()

const emit = defineEmits<{
  // The clicked result's wiki path. Parent forwards as 'select-path'
  // to its own consumers; emitting from here keeps the child decoupled
  // from the wiki_pages catalog lookup that the parent owns.
  (e: 'select-path', path: string): void
  (e: 'retry'): void
  (e: 'clear'): void
  // Broaden a scoped zero-result back to wiki-wide (clears the filter).
  (e: 'clear-filter'): void
}>()

function selectPath(path: string): void {
  emit('select-path', path)
}

// HTML-escape the FTS snippet body, THEN swap the marker pair for
// `<mark>` tags. Order matters — escaping after marker substitution
// would clobber the angle brackets we just inserted.
function renderSnippet(snippet: string): string {
  return snippet
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll(MARK_OPEN, '<mark>')
    .replaceAll(MARK_CLOSE, '</mark>')
}
</script>

<template>
  <div v-if="searching" class="flex justify-center p-8">
    <NSpin size="small" />
  </div>

  <NEmpty v-else-if="error" :description="error" class="p-8">
    <template #extra>
      <NButton size="small" @click="emit('retry')">Retry</NButton>
    </template>
  </NEmpty>

  <NEmpty
    v-else-if="results.length === 0"
    :description="activeFilterLabel ? `Nothing found in ${activeFilterLabel}.` : 'No pages match. Try a shorter query.'"
    class="p-8"
  >
    <template #extra>
      <div class="flex flex-wrap justify-center gap-2">
        <NButton
          v-if="activeFilterLabel"
          size="small"
          type="primary"
          data-testid="search-everywhere"
          @click="emit('clear-filter')"
          >Search everywhere</NButton
        >
        <NButton size="small" type="primary" ghost @click="emit('clear')"> Clear search </NButton>
      </div>
    </template>
  </NEmpty>

  <nav v-else class="px-3 py-3" data-testid="search-results">
    <!-- Each result is a real <button>: natively Tab-focusable
         with Enter/Space activation, instead of a fake `role="option"`
         listbox that declared an arrow-key contract it never implemented
         (WCAG 1.3.1 / 2.1.1). A plain list of buttons is honest about the
         interaction model and needs no roving-tabindex machinery. -->
    <ul class="list-none p-0 m-0 flex flex-col gap-1" aria-label="Search results">
      <li v-for="r in results" :key="r.path">
        <button
          type="button"
          data-testid="search-result"
          class="sidebar-item w-full text-left gap-1 text-white/70"
          @click="selectPath(r.path)"
        >
          <span class="text-sm leading-snug text-white">{{ r.title || r.path }}</span>
          <span class="text-xs text-white/50 leading-snug" v-html="renderSnippet(r.snippet)" />
        </button>
      </li>
    </ul>
  </nav>
</template>
